// Several inputs from one form (DECISIONS 2026-11-04 "several inputs at
// once"): brace ranges in a new input's text fields make one input per
// number, and an edit's changed settings go onto other inputs of its kind.

// {101..120}, {05..24}, {9..1}
const RANGE = /\{(\d+)\.\.(\d+)\}/g

// the most one form makes: a typo such as {1..1000} is refused, not made
export const MAX_MANY = 100

// the numbers a range makes, zero-padded when a bound is written with a
// leading zero ({01..12})
function numbers(a: string, b: string): string[] {
    const from = Number(a)
    const to = Number(b)
    const n = Math.abs(to - from) + 1
    if (n > MAX_MANY) throw new Error(`{${a}..${b}} makes ${n} inputs; at most ${MAX_MANY} at once.`)
    const width = /^0\d/.test(a) || /^0\d/.test(b) ? Math.max(a.length, b.length) : 0
    const step = from <= to ? 1 : -1
    return Array.from({ length: n }, (_, i) => String(from + i * step).padStart(width, '0'))
}

// expand makes one copy of body per number of its ranges, every range in
// every text field going up together: { name: 'Cam {1..3}', url:
// 'srt://10.0.0.{101..103}:5000' } is three, the second 'Cam 2' on .102.
// Without a range it is [body]. Ranges of different lengths are refused.
export function expand<T>(body: T): T[] {
    const json = JSON.stringify(body)
    const lists = [...json.matchAll(RANGE)].map((m) => numbers(m[1]!, m[2]!))
    if (!lists.length) return [body]
    const n = lists[0]!.length
    if (lists.some((l) => l.length !== n))
        throw new Error(`The ranges make different numbers of inputs (${lists.map((l) => l.length).join(', ')}).`)
    return Array.from({ length: n }, (_, i) => {
        let k = 0
        return JSON.parse(json.replace(RANGE, () => lists[k++]![i]!)) as T
    })
}

type Obj = Record<string, unknown>
const isObj = (x: unknown): x is Obj => typeof x === 'object' && x !== null && !Array.isArray(x)

// a setting that changed: where it is in the entity, and its new value
// (undefined: taken away)
export interface Setting {
    path: string[]
    value: unknown
}

// changed lists the settings that differ between two forms of an entity,
// leaf by leaf (a frame rate changed is not the size changed with it); a
// list is one setting
export function changed(before: unknown, after: unknown, path: string[] = []): Setting[] {
    if (isObj(before) && isObj(after))
        return [...new Set([...Object.keys(before), ...Object.keys(after)])].flatMap((k) =>
            changed(before[k], after[k], [...path, k]),
        )
    return JSON.stringify(before) === JSON.stringify(after) ? [] : [{ path, value: after }]
}

// settingText says a setting as the form shows it before applying it:
// "srt.latency_ms: 400"; a secret's value is never shown
export function settingText({ path, value }: Setting): string {
    const where = path.join('.')
    if (value === undefined || value === '') return `${where}: cleared`
    if (/passphrase|password|secret|key$/i.test(path[path.length - 1] ?? '')) return `${where}: (new, not shown)`
    return `${where}: ${typeof value === 'string' ? value : JSON.stringify(value)}`
}

// applied is a copy of entity with the settings set on it
export function applied<T>(entity: T, settings: Setting[]): T {
    const out = JSON.parse(JSON.stringify(entity)) as Obj
    for (const { path, value } of settings) {
        if (!path.length) continue
        let o = out
        for (const k of path.slice(0, -1)) o = isObj(o[k]) ? o[k] : (o[k] = {})
        // undefined: taken away (JSON leaves it out)
        o[path[path.length - 1]!] = value === undefined ? undefined : (JSON.parse(JSON.stringify(value)) as unknown)
    }
    return out as T
}
